import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import { delay, filter, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { ScanMode } from '../models';
import { ScannerAdapterServiceBase } from '../services/scanner-adapter.service.base';
import {
  barcodeScannedAction,
  changeScannerCameraSizeAction,
  closeIfScannerIsShownAction,
  closeScannerCameraAction,
  closeScannerCameraSuccessAction,
  pauseScanningAction,
  pauseScanningSuccessAction,
  restartScanningAction,
  resumeScanningAction,
  resumeScanningSuccessAction,
  scannerStateChangedAction,
  showScannerCameraAction,
  showTemporaryClosedScannerCameraAction,
  startScanningAction,
  startScanningSuccessAction,
  stopScanningAction,
  stopScanningSuccessAction,
} from './scanner.actions';
import { ScannerFacade } from './scanner.facade';

@Injectable()
export class ScannerEffects {
  protected showScanner$ = createEffect(() =>
    this.action$.pipe(
      ofType(showScannerCameraAction),
      // todo: find another alternative. This is due to scanner not closing and we try to reopen in again
      delay(300),
      switchMap((payload) => {
        this.scannerService.showScanner(payload.actionToDispatchOnBarcodeScanned);
        return of(startScanningAction());
      })
    )
  );

  protected showTemporaryClosedScanner$ = createEffect(() =>
    this.action$.pipe(
      ofType(showTemporaryClosedScannerCameraAction),
      withLatestFrom(this.scannerFacade.temporaryClosedDetails$),
      filter(([_, temporaryClosedDetails]) => temporaryClosedDetails.isTemporaryClosed),
      switchMap(([_, temporaryClosedDetails]) => {
        const scanMode = temporaryClosedDetails.isContinuousScan ? ScanMode.Continuous : ScanMode.Individual;
        return of(
          showScannerCameraAction({ actionToDispatchOnBarcodeScanned: temporaryClosedDetails.actionToDispatchOnBarcodeScanned, scanMode })
        );
      })
    )
  );

  protected closeScanner$ = createEffect(() =>
    this.action$.pipe(
      ofType(closeScannerCameraAction),
      switchMap(() => {
        this.scannerService.closeScaner();
        return of(closeScannerCameraSuccessAction());
      })
    )
  );

  protected closeIfScannerIsShown$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(closeIfScannerIsShownAction),
        withLatestFrom(this.scannerFacade.isScannerShown$),
        filter(([_, isScannerShown]) => isScannerShown),
        tap(() => {
          this.scannerFacade.closeScannerCamera(true);
        })
      ),
    { dispatch: false }
  );

  protected startScanner$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(startScanningAction),
        tap(() => {
          this.scannerService.startScanning();
        })
      ),
    {
      dispatch: false,
    }
  );

  protected resumeScanner$ = createEffect(() =>
    this.action$.pipe(
      ofType(resumeScanningAction),
      switchMap(() => {
        this.scannerService.resumeScanning();
        return of(resumeScanningSuccessAction());
      })
    )
  );

  protected pauseScanner$ = createEffect(() =>
    this.action$.pipe(
      ofType(pauseScanningAction),
      switchMap(() => {
        this.scannerService.pauseScanning();
        return of(pauseScanningSuccessAction());
      })
    )
  );

  protected restartScanner$ = createEffect(() =>
    this.action$.pipe(
      ofType(restartScanningAction),
      switchMap(() => {
        return of(stopScanningAction());
      })
    )
  );

  protected stopScanner$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(stopScanningAction),
        tap(() => {
          this.scannerService.stopScanning();
        })
      ),
    {
      dispatch: false,
    }
  );

  protected changeScannerCameraSize$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(changeScannerCameraSizeAction),
        tap(() => {
          // TODO: Add logic to decide based on what the camera will be changed
          this.scannerService.changeMargins();
        })
      ),
    {
      dispatch: false,
    }
  );

  protected stopScanningSuccess$ = createEffect(() =>
    this.action$.pipe(
      ofType(stopScanningSuccessAction),
      withLatestFrom(this.scannerFacade.scannerShouldRestart$),
      switchMap(([_, scannerShouldRestart]) => {
        if (scannerShouldRestart) {
          return of(startScanningAction());
        }

        return EMPTY;
      })
    )
  );

  protected scannerStateChanged$ = createEffect(() =>
    this.action$.pipe(
      ofType(scannerStateChangedAction),
      switchMap((payload) => {
        switch (payload.scannerState) {
          case Scandit.BarcodePicker.State.ACTIVE: {
            return of(startScanningSuccessAction());
          }
          case Scandit.BarcodePicker.State.PAUSED: {
            return of(pauseScanningSuccessAction());
          }
          case Scandit.BarcodePicker.State.STOPPED: {
            return of(stopScanningSuccessAction());
          }
          default: {
            return EMPTY;
          }
        }
      })
    )
  );

  protected barcodeScannedAction$ = createEffect(() =>
    this.action$.pipe(
      ofType(barcodeScannedAction),
      withLatestFrom(this.scannerFacade.isContinuousScan$),
      switchMap(([_, isContinuousScan]) => {
        if (isContinuousScan) {
          return EMPTY;
        }

        return of(pauseScanningAction());
      })
    )
  );

  constructor(private action$: Actions, private scannerService: ScannerAdapterServiceBase, private scannerFacade: ScannerFacade) {}
}
